import { createContext, useContext } from 'react'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { RESPONSIVE_LAYOUT_ENABLED } from '@/theme/breakpoints'
import type { LayoutMode } from '@/theme/breakpoints'

export interface LayoutModeContextValue {
  /** compact (< sm), medium (sm to lg), expanded (>= lg). */
  mode: LayoutMode
  /** True below `md`. Drives the admin block and the admin drawer swap. */
  isBelowMd: boolean
}

/**
 * Defaults to the desktop layout so any tree rendered without the provider
 * (unit tests, storybook-style harnesses) behaves exactly as it does today.
 */
export const LayoutModeContext = createContext<LayoutModeContextValue>({
  mode: 'expanded',
  isBelowMd: false,
})

export function useLayoutMode(): LayoutMode {
  return useContext(LayoutModeContext).mode
}

export function useIsCompact(): boolean {
  return useLayoutMode() === 'compact'
}

export function useIsExpanded(): boolean {
  return useLayoutMode() === 'expanded'
}

/** True for compact and medium: anywhere the desktop chrome is not rendered. */
export function useIsBelowExpanded(): boolean {
  return useLayoutMode() !== 'expanded'
}

/** True below `md`. Admin is blocked at these widths. */
export function useIsBelowMd(): boolean {
  return useContext(LayoutModeContext).isBelowMd
}

/**
 * Subscribes to matchMedia and derives the layout mode.
 * Called once, by LayoutModeProvider. Everything else reads the context so all
 * consumers agree on the mode within a single render and only one set of
 * media-query listeners exists.
 */
export function useComputedLayoutMode(): LayoutModeContextValue {
  const theme = useTheme()
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'))
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))
  const isExpanded = useMediaQuery(theme.breakpoints.up('lg'))

  // Mobile/tablet layouts are switched off, so every width reports the desktop mode.
  // The queries above stay subscribed so re-enabling the flag needs no change here.
  if (!RESPONSIVE_LAYOUT_ENABLED) return { mode: 'expanded', isBelowMd: false }

  const mode: LayoutMode = isCompact ? 'compact' : isExpanded ? 'expanded' : 'medium'
  return { mode, isBelowMd }
}
